/**
 * Feedback Floating Action Button (FAB)
 *
 * Persistent circular button at the bottom-right of the screen.
 * Opens a slide-up panel with the FeedbackForm. Automatically captures the
 * current page path so admins can see where feedback was submitted from.
 */

import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { MessageSquarePlus, X } from 'lucide-react';
import { FeedbackForm } from '../organisms/FeedbackForm';
import { feedbackAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../i18n';
import type { FeedbackFormData } from '../organisms/FeedbackForm';
import { cn } from '../ui/utils';

export function FeedbackFAB() {
  const [isOpen, setIsOpen] = useState(false);
  const { apiKey } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const panelRef = useRef<HTMLDivElement>(null);

  // Close panel on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Close panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (isOpen && panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSubmit = async (data: FeedbackFormData) => {
    if (!apiKey) throw new Error('Not authenticated');

    // Auto-append current page to component field
    const pageLabel = location.pathname === '/' ? 'Dashboard' : location.pathname;
    const component = data.component
      ? `${data.component} (${pageLabel})`
      : pageLabel;

    await feedbackAPI.submit(apiKey, { ...data, component });

    // Close panel after short delay so user sees success message
    setTimeout(() => setIsOpen(false), 2000);
  };

  return (
    <>
      {/* Backdrop overlay when panel is open */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/20 dark:bg-black/40 z-40 transition-opacity" />
      )}

      <div ref={panelRef} className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3 pointer-events-none">
        {/* Feedback panel */}
        <div
          className={cn(
            'w-[380px] max-h-[calc(100dvh-120px)] overflow-y-auto',
            'rounded-xl border border-gray-200 dark:border-white/10',
            'bg-white dark:bg-[#111] shadow-2xl',
            'transition-all duration-200 origin-bottom-right',
            isOpen
              ? 'scale-100 opacity-100 translate-y-0 pointer-events-auto'
              : 'scale-95 opacity-0 translate-y-2 pointer-events-none'
          )}
          aria-hidden={!isOpen}
        >
          {/* Panel header */}
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <MessageSquarePlus className="w-4 h-4 text-fuchsia-500" />
              {t('feedback.sendFeedback')}
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              aria-label={t('feedback.closeFeedback')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="px-4 pb-1">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('feedback.shareIdeas')}
            </p>
          </div>
          {/* Form content — only render when open for perf */}
          {isOpen && (
            <div className="px-4 pb-4">
              <FeedbackForm
                onSubmit={handleSubmit}
                compact
                noCard
              />
            </div>
          )}
        </div>

        {/* FAB bubble */}
        <button
          onClick={() => setIsOpen(prev => !prev)}
          className={cn(
            'w-12 h-12 rounded-full shadow-lg flex items-center justify-center',
            'pointer-events-auto',
            'transition-all duration-200 hover:scale-105 active:scale-95',
            'focus:outline-none focus:ring-2 focus:ring-fuchsia-500 focus:ring-offset-2 dark:focus:ring-offset-[#050505]',
            isOpen
              ? 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rotate-0'
              : 'bg-fuchsia-500 hover:bg-fuchsia-600 text-white'
          )}
          aria-label={isOpen ? t('feedback.closeFeedback') : t('feedback.sendFeedback')}
          title={t('feedback.sendFeedback')}
        >
          {isOpen ? (
            <X className="w-5 h-5" />
          ) : (
            <MessageSquarePlus className="w-5 h-5" />
          )}
        </button>
      </div>
    </>
  );
}
